/**
 * Company: SPHMedia
 * Description: [AI Summary Article Modal]
 */

import TranslateModalClose from "@assets/article/translateModalClose.svg";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import { FieldWrapper } from "@components/Modules/Translation/Elements/FieldWrapper";
import { pubPageAdTargeting } from "@config/ads";
import {
  AISummaryAdsLbEnglish,
  AISummaryAdsLbMalay,
} from "@elements/Advertisement/variant/AISummaryAds";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import Dompurify from "dompurify";
import React, { useState } from "react";
import { ReactElement } from "react";

export type AISummaryModalProps = {
  id: string;
  adsSectionName: string;
  english?: string[];
  malay?: string[];
  isOpen: boolean;
  onClose: () => void;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  pageNumber: number;
};

/**
 * AISummaryModal
 * Description: [Opens AI summary content of article as a modal]
 * @param props - The props required by the component
 * @returns The rendered React element
 */
export default function AISummaryModal(
  props: AISummaryModalProps,
): ReactElement {
  const [showMalay, setShowMalay] = useState<boolean>(true);
  const {
    id,
    adsSectionName,
    english,
    malay,
    onClose,
    gsChannels,
    articleSlotTarget,
    pageNumber,
  } = props;
  const pageAdTargetEnglish = PageAdTargetingTypeEnum.SUMMARY_EN;
  const PubPageAdTargetEnglish = pubPageAdTargeting[
    pageAdTargetEnglish
  ] as AdTargetingType<ArticleAdTargetKeys, string>;
  const pageAdTargetMalay = PageAdTargetingTypeEnum.SUMMARY_MS;
  const PubPageAdTargetMalay = pubPageAdTargeting[
    pageAdTargetMalay
  ] as AdTargetingType<ArticleAdTargetKeys, string>;

  const bhCustomEvent = useBHCustomEventAnalyticsCall();

  const handleMalayClick = () => {
    setShowMalay(true);
    bhCustomEvent(
      "ai summary malay",
      "click",
      window.location.href,
      undefined,
      undefined,
      CD_LANGUAGE.MALAY,
    );
  };

  const handleEnglishClick = () => {
    setShowMalay(false);
    bhCustomEvent(
      "ai summary english",
      "click",
      window.location.href,
      undefined,
      undefined,
      CD_LANGUAGE.ENGLISH,
    );
  };

  return (
    <div
      id={`trans-${id}`}
      className="flex flex-col gap-[12px] items-center mb-md"
    >
      <div className="flex justify-between items-center w-full h-[48px] lg:h-[80px] lg:max-w-[860px] mx-auto bg-bg-primary px-sm lg:px-0 sticky top-0">
        <div className="flex flex-col gap-xs">
          <span className="text-brand-primary text-base font-primary font-medium">
            Summary by AI
          </span>
        </div>
        <div className="flex justify-between items-center gap-[16px]">
          <div>
            <button
              className={`w-[70px] lg:w-[120px] h-[32px] font-primary text-xs font-medium ${showMalay ? "bg-brand-primary text-text-inverse" : "bg-neutral-200 text-text-body hover:bg-neutral-200 hover:text-text-body"}`}
              onClick={handleMalayClick}
              style={
                showMalay
                  ? { boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset" }
                  : undefined
              }
            >
              Malay
            </button>
            <button
              className={`w-[70px] lg:w-[120px] h-[32px] font-primary text-xs font-medium ${!showMalay ? "bg-brand-primary text-text-inverse" : "bg-neutral-200 text-text-body hover:bg-neutral-200 hover:text-text-body"}`}
              onClick={handleEnglishClick}
              style={
                !showMalay
                  ? { boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset" }
                  : undefined
              }
            >
              English
            </button>
          </div>
          <button className="" onClick={onClose}>
            <img
              src={TranslateModalClose}
              alt="translate-close"
              width={23}
              height={23}
            />
          </button>
        </div>
      </div>
      <div className="flex flex-col w-full gap-xs">
        {showMalay && malay && (
          <>
            {malay.map((para, index) => (
              <React.Fragment key={index}>
                <FieldWrapper className="w-full">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: Dompurify.sanitize(para),
                    }}
                    className="text-base font-primary font-medium text-text-body leading-[24px] px-sm lg:px-0"
                  />
                </FieldWrapper>
              </React.Fragment>
            ))}
            <AdsContainer>
              <AISummaryAdsLbMalay
                uniqueSectionName={adsSectionName}
                index={pageNumber}
                gsChannels={gsChannels}
                articleSlotTarget={
                  articleSlotTarget
                    ? [...articleSlotTarget, PubPageAdTargetMalay]
                    : [PubPageAdTargetMalay]
                }
              />
            </AdsContainer>
          </>
        )}
        {!showMalay && english && (
          <>
            {english.map((para, index) => (
              <React.Fragment key={index}>
                <FieldWrapper className="w-full">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: Dompurify.sanitize(para),
                    }}
                    className="text-base font-primary font-medium text-text-body leading-[24px] px-sm lg:px-0"
                  />
                </FieldWrapper>
              </React.Fragment>
            ))}
            <AdsContainer>
              <AISummaryAdsLbEnglish
                uniqueSectionName={adsSectionName}
                index={pageNumber}
                gsChannels={gsChannels}
                articleSlotTarget={
                  articleSlotTarget
                    ? [...articleSlotTarget, PubPageAdTargetEnglish]
                    : [PubPageAdTargetEnglish]
                }
              />
            </AdsContainer>
          </>
        )}
      </div>
    </div>
  );
}
